import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile } from "@ffmpeg/util";
import { uploadFileToS3Complete } from "../api/s3";

/**
 * Processes an audio file to change its playback speed using FFmpeg's atempo filter
 *
 * @param audioBlob The audio blob to process
 * @param speed The speed factor (0.8 to 1.2)
 * @param uploadToS3 Whether to upload the processed audio to S3 (default: false)
 * @param onProgress Optional callback for conversion progress
 * @returns Promise resolving to the processed audio blob and optionally an S3 URL
 */
export const changeAudioSpeed = async (
    audioBlob: Blob,
    speed: number,
    uploadToS3: boolean = false,
    onProgress?: (status: { message: string; progress?: number }) => void,
): Promise<{ blob: Blob; url: string }> => {
    const ffmpeg = new FFmpeg();

    const validSpeed = Math.max(0.8, Math.min(1.2, speed));
    onProgress?.({ message: "Loading FFmpeg", progress: 0.1 });

    await ffmpeg.load();

    // Detect file type
    let fileType = audioBlob.type.split("/")[1] || "mp3";
    if (audioBlob.type === "application/octet-stream" || !["mp3", "wav", "aac", "m4a"].includes(fileType)) {
        fileType = "mp3";
    }

    const inputFileName = `input.${fileType}`;
    const outputFileName = `output.${fileType}`;

    const inputData = await fetchFile(audioBlob);

    await ffmpeg.writeFile(inputFileName, inputData);

    onProgress?.({ message: "Processing audio", progress: 0.5 });

    await ffmpeg.exec([
        "-i",
        inputFileName,
        "-filter:a",
        `atempo=${validSpeed}`,
        "-c:a",
        fileType === "mp3" ? "libmp3lame" : "aac",
        outputFileName,
    ]);

    onProgress?.({ message: "Reading output file", progress: 0.9 });

    const data = await ffmpeg.readFile(outputFileName);
    let blob: Blob;

    if (data instanceof Uint8Array) {
        blob = new Blob([data], { type: `audio/${fileType}` });
    } else if (typeof data === "string") {
        const encoder = new TextEncoder();
        const uint8Array = encoder.encode(data);
        blob = new Blob([uint8Array], { type: `audio/${fileType}` });
    } else {
        throw new Error("Unsupported file data format from ffmpeg");
    }

    let url = "";

    if (uploadToS3) {
        onProgress?.({ message: "Uploading to S3", progress: 0.95 });
        try {
            // Create a File object from the Blob for S3 upload
            const file = new File([blob], `speed_${validSpeed.toFixed(1)}_${Date.now()}.${fileType}`, {
                type: `audio/${fileType}`,
            });

            // Upload to S3 using the new S3 service
            const token = localStorage.getItem("token");
            if (!token) {
                throw new Error("No authentication token found");
            }

            url = await uploadFileToS3Complete(
                {
                    file,
                    isPublic: true, // Public files for general uploads
                    isFolder: false, // No folder structure for general uploads
                },
                token,
            );
        } catch (error) {
            console.error("[S3] Upload failed:", error);
            // Fall back to creating a local object URL
            url = URL.createObjectURL(blob);
        }
    } else {
        // Create a local object URL if not uploading to S3
        url = URL.createObjectURL(blob);
    }

    onProgress?.({ message: "Finished", progress: 1.0 });

    await ffmpeg.terminate();

    return { blob, url };
};
